import { LitElement, html } from 'lit';
import { checkProductAvailability } from '@Root/spree/api';
import { imagebossUrl } from '@Helpers/imageboss';

class DirectProductIndexCard extends LitElement {
  static properties = {
    product: { type: Object },
    index: { type: Number },
    _quantity: { type: Number },
    _productAvailabilityCheckConducted: { type: Boolean },
  };

  createRenderRoot() {
    // Don't use shadowRoot so parent component can style
    return this;
  }

  constructor() {
    super();

    this._productAvailabilityCheckConducted = false;
  }

  render() {
    return html`
      <div class="product-wrapper">
        ${this.productDetailsHtml} ${this.productCtaHtml}
      </div>
    `;
  }

  get productDetailsHtml() {
    return html`
      <a class="product-details" href="${this.product.path || ''}">
        <img src="${this.productImageUrl}" />
        <p class="product-sku description">
          ${this.optionalDetailRow(this.product.sku, 'SKU')}
        </p>
        ${this.productHeaderHTML}
        <div class="optional-details">
          ${this.optionalDetailRow(this.product.display_price, 'Price')}
          ${this.optionalDetailRow(this.product.lead_time, 'Lead Time')}
        </div>
      </a>
    `;
  }

  get productCtaHtml() {
    if (this.product.is_cut_to_order) return this.pageLinkCtaHtml;

    return this.addToCartCtaHtml;
  }

  get pageLinkCtaHtml() {
    return html`
      <div class="order-ctas">
        <a href="${this.product.path}">
          <cta-button
            class="submit-button"
            cta-data=${this.configureCTOCtaData}
          >
          </cta-button>
        </a>
      </div>
    `;
  }

  get productHeaderHTML() {
    return this.productHeader
      ? html`
          <div class="product-header">
            <p class="header">${this.productHeader}</p>
            ${this.productSubheaderHTML}
          </div>
        `
      : html``;
  }

  get productSubheaderHTML() {
    return this.product.subheader
      ? html`
          <p class="subheader description">${this.product.subheader}</p>
        `
      : html``;
  }

  get addToCartCtaHtml() {
    return html`
      <div class="order-ctas">
        <input
          id="qty"
          name="qty"
          placeholder="Qty"
          type="number"
          min="0"
          step="1"
          @keyup="${this.inputQuantityChanged}"
        />
        <cta-button
          class="submit-button"
          cta-data=${this.addToCartCtaData}
          @click="${this.addToCart}"
          ?disabled="${this.addToCartDisabled}"
        >
        </cta-button>
      </div>
    `;
  }

  get addToCartCtaData() {
    return JSON.stringify({
      text: 'Add to Cart',
      isPrimary: true,
      fullWidth: true,
    });
  }

  addToCart(_event) {
    const addToCartEvent = new CustomEvent('addToCart', {
      detail: {
        product: this.product,
        quantity: this._quantity,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(addToCartEvent);
  }

  get productImageUrl() {
    return imagebossUrl(this.product.image_url, { height: '300' });
  }

  optionalDetailRow(property, propertyName) {
    if (!property || !propertyName) return html``;
    return html`
      <div class="optional-row-wrapper">
        <span class="optional-row-header">${propertyName}</span>
        <span class="optional-row-value">${property}</span>
      </div>
    `;
  }

  get configureCTOCtaData() {
    return JSON.stringify({
      text: 'Configure',
      isPrimary: true,
      fullWidth: true,
    });
  }

  async inputQuantityChanged(_event) {
    if (!this._productAvailabilityCheckConducted) {
      const productAvailabilityResponse = await checkProductAvailability(
        this.product.spree_product.id
      );

      this.product._isAvailable = !productAvailabilityResponse.isFail();
      this._productAvailabilityCheckConducted = true;
    }

    this._quantity = Number(this.inputQuantity);
  }

  get inputQuantity() {
    if (!this.quantityInput?.value) return 0;

    return this.quantityInput.value;
  }

  get quantityInput() {
    return this.renderRoot.querySelector('#qty');
  }

  get addToCartDisabled() {
    return !this.hasPositiveQuantity || !this.productIsAvailable;
  }

  get hasPositiveQuantity() {
    return this._quantity > 0;
  }

  get productIsAvailable() {
    return this.product._isAvailable;
  }

  get productHeader() {
    // use the product name as a fallback
    return this.product.header || this.product.name;
  }
}
customElements.define('direct-product-index-card', DirectProductIndexCard);
